<script>
    import Svg from "vpap/svelte/Svg.svelte";
    import {onMount} from "svelte";
    import {scaleLinear} from "d3-scale";
    import {line} from "d3-shape";
    import {draw} from "svelte/transition";
    import {linear} from "svelte/easing";

    export let dataUrl;

    // chart properties
    let chartWidth;
    $: isMobile = chartWidth && chartWidth <= 650;
    $: chartHeight = isMobile ? chartWidth : chartWidth * (9/16);
    const barWidth = 25;
    const barPadding = 3;
    $: barChartHeight = industries ? industries.length * (barWidth + barPadding) + xAxisHeight : undefined;
    $: barChartOffset = isMobile ? 190 : 230;
    const chartOffset = 50;
    const chartOffsetHeight = 20;
    const xAxisHeight = 40;
    $: detailBarWidth = (chartWidth - chartOffset - 15) / 12;
    $: barChartTicks = chartWidth < 400 ? [-0.2, 0] : isMobile ? [-0.2, -0.1, 0, 0.05] : [-0.2, -0.15, -0.1, -0.05, 0, 0.05];
    $: barChartScale = scaleLinear()
        .domain([Math.min(...barChartTicks), Math.max(...barChartTicks)])
        .range([barChartOffset, chartWidth - 15]);

    // chart data
    let data;
    let industries;
    let industryData;
    let industryTotals;
    let industryPath;
    let selectedIndustry = 0;
    let maxDetailChange;
    let minDetailChange;
    let detailYScale;
    let detailYTicks;
    let detailXTicks;

    function buildOverallTotals(allData) {
        let totals = new Map();
        for (const item of allData) {
            if (totals.has(item.industry)) {
                if (item.month === 1) {
                    totals.set(item.industry, Object.assign(totals.get(item.industry), {start: item.value}));
                } else if (item.month === 12) {
                    totals.set(item.industry, Object.assign(totals.get(item.industry), {end: item.value}));
                }
            } else {
                totals.set(item.industry, {start: item.month === 1 ? item.value : undefined, end: undefined});
            }
        }
        for (const [key, value] of totals) {
            const pctChange = (value.end - value.start) / value.start;
            totals.set(key, Object.assign(value, {pct_change: pctChange}));
        }
        return totals;
    }

    $: if (data) {
        industryTotals = buildOverallTotals(data);
        industries = data.filter(d => d.month === 1).map(d => d.industry);
        industryData = data.filter(d => d.industry === industries[selectedIndustry]);
        maxDetailChange = Math.max(...industryData.map(d => d.value));
        minDetailChange = Math.min(...industryData.map(d => d.value));
        detailYScale = scaleLinear()
            .domain([minDetailChange, maxDetailChange]).nice()
            .range([chartHeight - xAxisHeight, chartOffsetHeight]);
        detailYTicks = detailYScale.ticks(4);
        industryPath = line()(industryData.map((d, i) => [chartOffset + (i * detailBarWidth), detailYScale(d.value)]));
        detailXTicks = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
            .filter((_d, i) =>  i % (isMobile ? 3 : 2) === 0);
    }

    onMount(async () => {
        let res = await fetch(dataUrl)
            .then(resp => resp.json());
        data = res;
    })
</script>

<template>
    {#if data && industries}
    <div class="industry-totals">
        <h2>Industry Employment Change</h2>
        <p class="lead">The percent change in Virginia employment from Jan. 2020 to Dec. 2020.</p>
        <div class="industry-bar">
            {#if barChartHeight && chartWidth}
            <Svg height={barChartHeight} width={chartWidth}
                description="A bar chart showing the percent change in employment across industries from Jan. 2020 to Dec. 2020">

                <g class="axis-bar-y">
                    {#each barChartTicks as tick}
                    <line 
                        x1={barChartScale(tick)} 
                        x2={barChartScale(tick)} 
                        y2={barChartHeight - xAxisHeight} 
                        stroke-width={tick === 0 ? 2 : 0.75}
                        class:baseline={tick === 0}
                    ></line>
                    {/each}
                </g>
                <g class="axis-x">
                    {#each barChartTicks as tick}
                    <text x={barChartScale(tick)} y={barChartHeight - 20}>{tick * 100}%</text>
                    {/each}
                </g>
                <g class="bars">
                    {#each [...industryTotals].slice().sort((a, b) => b[1].pct_change - a[1].pct_change) as [industry, totals], idx}
                    <text x={barChartOffset - 5} y={(barWidth + barPadding) * idx + barPadding + 17}>{industry}</text>
                    <rect
                        class:bar-positive={totals.pct_change > 0}
                        class:bar-negative={totals.pct_change < 0}
                        y={(barWidth + barPadding) * idx + barPadding}
                        height={barWidth} 
                        x={totals.pct_change > 0 ? barChartScale(0) + 1 : barChartScale(totals.pct_change) - 1}
                        width={totals.pct_change > 0 ? barChartScale(totals.pct_change) - barChartScale(0) : barChartScale(0) - barChartScale(totals.pct_change)}
                    ></rect>
                    {/each}
                </g>
            </Svg>
            {/if}
        </div>
    </div>
    <div class="industry-detail">
        <h2>Employment Changes by Month</h2>
        <p class="lead">The change in {industries[selectedIndustry]} jobs
            since January
        </p>
        <div class="detail-legend">
            <label for="industry-options">Select an industry:</label>
            <select id="industry-options" bind:value={selectedIndustry}>
                {#each industries as industry, idx}
                <option value={idx}>{industry}</option>
                {/each}
            </select>
        </div>
        <div class="industry-bar" bind:clientWidth={chartWidth}>
            {#if chartWidth && chartHeight}
            <Svg 
                width={chartWidth} 
                height={chartHeight} 
                description={`A bar chart showing the percentage change in ${industries[selectedIndustry]} industry jobs for each month from January 2020 to December 2020`}
            >
            <g class="axis">
                <line x1={chartOffset} x2={chartOffset} y1={chartOffsetHeight} y2={chartHeight - xAxisHeight}></line>
                {#each detailYTicks as tick}
                <text x="5" y={detailYScale(tick)} transform="translate(0, 6)">{tick}k</text>
                <line x1={chartOffset} y1={detailYScale(tick)} y2={detailYScale(tick)} x2={chartWidth}></line>
                {/each}
            </g>
            <g class="axis-x">
                {#each detailXTicks as tick, i}
                <text 
                    x={chartOffset + ((i * (12 / detailXTicks.length)) * detailBarWidth)}
                    y={chartHeight - xAxisHeight + 25}
                >{tick}</text>
                {/each}
            </g>
                <g class="line-chart">
                    {#key selectedIndustry}
                    <path
                        class="line-path"
                        d={industryPath}
                        in:draw="{{duration: 1000, easing: linear}}">
                    </path>
                    {/key}
                </g>
            </Svg>
            {/if}
        </div>
    </div>
    {/if}
</template>

<style lang="scss">
    $increase: #00a300;
    $decrease: #a30000;

    .bar-positive {
        fill: $increase;
    }

    .bar-negative {
        fill: $decrease;
    }

    line {
            stroke: #c2c2c2;
    }

    .industry-bar :global(svg) {
        background-color: rgb(235,235,235);
    }

    .detail-legend {
        margin-bottom: 1.5em;

        & label {
            display: block;
            font-size: 1.5em;
        }
    }

    .axis-x {
        & text {
            text-anchor: middle;
        }
    }

    .line-chart {
        & path {
            stroke: $orange;
            stroke-width: 5;
            fill: none;
        }
    }

    .bars {
        text-anchor: end;
    }

    .baseline {
        stroke: black;
    }

    @media only screen and (max-width: 500px) {
        text {
            font-size: 0.8rem;
        }
    }
</style>